import React, { useState } from 'react';
import { X, Mail, Check, AlertCircle, Loader2, Copy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { useWines } from '@/contexts/WinesContext';

interface GenerateConnectionCodeDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GenerateConnectionCodeDialog: React.FC<GenerateConnectionCodeDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { generateConnectionCode } = useWines();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleClose = () => {
    setEmail('');
    setLoading(false);
    setGeneratedCode(null);
    setError(null);
    setCopied(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Inserisci un indirizzo email');
      return;
    }

    // Validazione email base
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Inserisci un indirizzo email valido');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const code = await generateConnectionCode(email.trim());
      setGeneratedCode(code);
    } catch (err) {
      console.error('Error generating connection code:', err);
      setError(err instanceof Error ? err.message : 'Errore durante la generazione del codice');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    if (!generatedCode) return;

    try {
      await navigator.clipboard.writeText(generatedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying code:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 top-0 bg-black/50 flex items-center justify-center z-[100] p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {generatedCode ? 'Codice Generato' : 'Invita Utente'}
              </h2>
              {!generatedCode && (
                <p className="text-sm text-gray-600 mt-1">
                  Genera un codice per connettere un nuovo utente
                </p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 overflow-y-auto flex-1">
            {generatedCode ? (
              /* Success State - Show Code */
              <div className="space-y-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    Condividi questo codice con <strong>{email}</strong>
                  </p>
                </div>

                {/* Code Display */}
                <Card padding="lg" className="bg-gray-50">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-3">Codice Invito</p>
                    <div className="text-3xl font-mono font-bold text-wine-purple tracking-wider mb-4">
                      {generatedCode}
                    </div>
                    <Button
                      icon={copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      onClick={handleCopyCode}
                      variant={copied ? 'primary' : 'outline'}
                      fullWidth
                    >
                      {copied ? 'Copiato!' : 'Copia Codice'}
                    </Button>
                  </div>
                </Card>

                {/* Info */}
                <div className="space-y-3">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      <strong>Nota:</strong> Il codice scade dopo 7 giorni. L'utente dovrà
                      inserirlo nella sezione "Accetta Invito" del suo profilo.
                    </p>
                  </div>

                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-900">
                      <strong>Importante:</strong> Condividi il codice solo con persone di
                      fiducia. Una volta connessi, potrete condividervi i vini.
                    </p>
                  </div>
                </div>

                {/* Close Button */}
                <Button onClick={handleClose} fullWidth>
                  Chiudi
                </Button>
              </div>
            ) : (
              /* Input Form */
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email dell'utente da invitare
                  </label>
                  <Input
                    icon={Mail}
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    placeholder="esempio@email.com"
                    disabled={loading}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    L'utente deve già avere un account su myWine
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <Card padding="md" className="bg-red-50 border border-red-200">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-900">Errore</p>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Info */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900">
                    Verrà generato un codice di 8 caratteri che potrai condividere con
                    l'utente. Il codice scadrà dopo 7 giorni.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    fullWidth
                    disabled={loading}
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    fullWidth
                    loading={loading}
                    disabled={!email.trim() || loading}
                    icon={loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  >
                    Genera Codice
                  </Button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
